import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import TicketTable from "./TicketTable";
import TicketModal from "./TicketModal";
import {
  getAdminTicketsList,
  adminReplyTicket,
  changeTicketStatus,
  markMessagesAsSeen,
} from "../../Backend/supportfunction";

import { useGlobalContext } from "../../ContextApi/GlobalContext";
import {
  pageShellClass,
  pageContentClass,
} from "../../Container/PageShell/pageShellClasses";

const Support = () => {
  const { state } = useGlobalContext();
  const adminUser = state?.companyData || {};
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const fetchAllTickets = async () => {
    setLoading(true);
    try {
      const result = await getAdminTicketsList();
      if (result.success) {
        setTickets(Array.isArray(result.data) ? result.data : []);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTickets();
  }, []);

  // --- 2. OPEN MODAL & MARK SEEN ---
  const handleRowClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);

    // Background sync: mark messages from user as seen
    markMessagesAsSeen(ticket.id);

    setTickets((prev) =>
      prev.map((t) => {
        if (t.id === ticket.id) {
          const updatedMessages = t.messages?.map((msg) =>
            msg.role === "user" ? { ...msg, status: "seen" } : msg,
          );
          return { ...t, messages: updatedMessages };
        }
        return t;
      }),
    );
  };

  // --- 3. ADMIN REPLY LOGIC ---
  const handleAdminSendMessage = async (ticketId, text, files) => {
    const result = await adminReplyTicket(ticketId, text, files);

    if (result.success) {
      // Optimistic Update for current modal
      const newMsg = {
        id: `msg_${Date.now()}`,
        role: "admin",
        senderName: adminUser.name || "System Admin",
        message: text,
        timestamp: new Date().toISOString(),
        status: "unseen", // User will see this as unseen initially
      };

      setSelectedTicket((prev) => ({
        ...prev,
        messages: [...(prev?.messages || []), newMsg],
        status: "In Progress", // Status auto-updates in DB on reply
      }));

      // Refresh list to sync with DB
      fetchAllTickets();
    } else {
      Swal.fire("Error", result.error || "Failed to send response.", "error");
    }
  };

  // --- 4. STATUS CHANGE LOGIC ---
  const handleUpdateStatus = async (ticketId, newStatus) => {
    const result = await changeTicketStatus(ticketId, newStatus);
    if (result.success) {
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId
            ? { ...t, status: newStatus, lastUpdated: new Date().toISOString() }
            : t,
        ),
      );
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket((prev) => ({ ...prev, status: newStatus }));
      }
      Swal.fire({
        icon: "success",
        title: `Status: ${newStatus}`,
        toast: true,
        position: "top-end",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  return (
    <div className={pageShellClass}>
      <div className={pageContentClass}>
        {/* HEADER */}
        <div className="mb-6 lg:mb-10 hidden lg:block">
          <h1 className="text-3xl font-black text-[#0B1C2D] tracking-tighter uppercase">
            Admin <span className="text-red-500">Support Hub</span>
          </h1>
          <p className="text-slate-400 text-sm font-medium mt-1">
            Managing {tickets.length} global inquiries
          </p>
        </div>

        {/* TABLE CONTAINER */}
        <div className="bg-white rounded-2xl lg:rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden min-h-[400px] lg:min-h-[600px]">
          {loading ? (
            <div className="h-[400px] lg:h-[600px] flex items-center justify-center text-slate-400 font-bold uppercase tracking-widest animate-pulse text-xs sm:text-base">
              Syncing global tickets...
            </div>
          ) : (
            <TicketTable
              tickets={tickets}
              onRowClick={handleRowClick}
              isAdminView={true}
              onUpdateStatus={handleUpdateStatus}
            />
          )}
        </div>
      </div>

      {/* CHAT MODAL */}
      <TicketModal
        ticket={selectedTicket}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSendMessage={handleAdminSendMessage}
        onUpdateStatus={handleUpdateStatus}
        isAdminView={true}
      />
    </div>
  );
};

export default Support;
